import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ChevronRight, Check, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GUIDE_IMG, MEDIA } from "@/lib/media";
import ProgressRing from "@/components/ProgressRing";

export default function Challenge() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ch, setCh] = useState(null);
  const [progress, setProgress] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const loadProgress = useCallback(() => {
    if (user) api.get(`/challenges/${slug}/progress`).then((r) => setProgress(r.data)).catch(() => {});
  }, [slug, user]);

  useEffect(() => { api.get(`/challenges/${slug}`).then((r) => setCh(r.data)).catch(() => setNotFound(true)); }, [slug]);
  useEffect(() => { loadProgress(); }, [loadProgress]);

  const start = async () => {
    if (!user) { navigate("/signin"); return; }
    const { data } = await api.post(`/challenges/${slug}/start`);
    setProgress(data);
  };

  const toggleDay = async (day) => {
    if (!user) { navigate("/signin"); return; }
    const { data } = await api.post(`/challenges/${slug}/toggle-day`, { day });
    setProgress((p) => ({ ...(p || {}), started: true, completed_days: data.completed_days }));
  };

  if (notFound) return <div className="max-w-3xl mx-auto py-24 px-6 text-center text-mutedplum">Challenge not found. <Link to="/challenges" className="text-teal font-semibold">All challenges</Link></div>;
  if (!ch) return <div className="py-24 text-center text-mutedplum">Loading…</div>;

  const done = progress?.completed_days || [];
  const started = progress?.started;

  return (
    <div>
      <div className="relative h-64 sm:h-72 overflow-hidden">
        <img src={GUIDE_IMG[ch.image_key] || MEDIA.hero} alt={ch.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-plum/60" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 h-full flex flex-col justify-end pb-8">
          <nav className="text-cream/70 text-sm flex items-center gap-1.5 mb-2">
            <Link to="/challenges" className="hover:text-cream">Challenges</Link><ChevronRight className="h-4 w-4" /><span className="text-cream">{ch.duration_days} days</span>
          </nav>
          <h1 className="font-display text-3xl sm:text-5xl text-cream leading-tight">{ch.title}</h1>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <p className="text-lg text-mutedplum leading-relaxed">{ch.description}</p>
          <div className="mt-4 rounded-2xl bg-rose-soft border border-rose/40 p-4 flex items-start gap-2.5">
            <ShieldAlert className="h-5 w-5 text-plum mt-0.5 shrink-0" />
            <p className="text-sm text-plum-ink/80">Move at your own pace and rest when you need to. This isn't medical advice—check with your doctor if anything feels off.</p>
          </div>

          <h2 className="mt-8 font-display text-2xl text-plum mb-4">Your daily plan</h2>
          <div className="space-y-2" data-testid="challenge-days">
            {ch.days.map((label, idx) => {
              const day = idx + 1;
              const isDone = done.includes(day);
              return (
                <button key={day} onClick={() => toggleDay(day)} data-testid={`day-${day}`}
                  className={`w-full flex items-center gap-3 rounded-2xl border-2 p-4 text-left transition-colors
                    ${isDone ? "border-teal bg-teal/10" : "border-rose/40 bg-white hover:border-teal/60"}`}>
                  <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full shrink-0 ${isDone ? "bg-teal text-white" : "bg-rose/30 text-plum"}`}>
                    {isDone ? <Check className="h-4 w-4" /> : <span className="text-sm font-bold">{day}</span>}
                  </span>
                  <span className={`text-[15px] ${isDone ? "text-teal font-medium line-through/0" : "text-plum-ink"}`}>{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <aside>
          <div className="bg-white rounded-3xl border border-rose/30 shadow-card p-6 text-center sticky top-24">
            <ProgressRing value={done.length} total={ch.duration_days} label="days done" />
            {!started ? (
              <button onClick={start} data-testid="start-challenge-btn"
                className="mt-6 w-full rounded-full bg-teal px-6 py-3.5 font-semibold text-white hover:bg-teal-dark transition-colors">
                Start this challenge
              </button>
            ) : (
              <p className="mt-6 text-mutedplum text-sm">You're on your way—tick off each day as you go. 💪</p>
            )}
            {done.length === ch.duration_days && ch.duration_days > 0 && (
              <p className="mt-4 font-script text-2xl text-teal" data-testid="challenge-complete">You did it—amazing!</p>
            )}
            <Link to="/community/motivation-and-accountability" className="mt-4 inline-block text-sm text-teal font-semibold" data-testid="challenge-thread-link">
              Share your progress →
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
